import React, { useState } from 'react';
import {
    Box, Flex, Heading, Text, Button, Badge,
    Tabs, TabList, TabPanels, Tab, TabPanel,
    Stack, Divider, Alert, AlertIcon, AlertDescription,
    Code, HStack, useColorModeValue,
    Tag, TagLabel, TagLeftIcon, useToast,
} from '@chakra-ui/react';
import {
    MdArrowBack, MdCheckCircle, MdWarning,
    MdContentCopy, MdInfo,
} from 'react-icons/md';
import { SiGooglesheets } from 'react-icons/si';
import { FaNetworkWired } from 'react-icons/fa';
import { MdApi } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import templateFile from '../assets/Files/orders_to_update_webhook.csv';
import { MdDownload } from 'react-icons/md';

// ─── CopyBlock ────────────────────────────────────────────────────────────────
const CopyBlock = ({ value, label }) => {
    const toast = useToast();
    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        toast({ title: 'Copié !', description: `${label} copié`, status: 'success', duration: 2000, isClosable: true });
    };
    return (
        <Flex
            bg={useColorModeValue('gray.100', 'gray.800')}
            p={3}
            rounded="md"
            align="center"
            justify="space-between"
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.700')}
        >
            <Code bg="transparent" fontSize="sm" wordBreak="break-all" colorScheme="blue">
                {value}
            </Code>
            <Button size="sm" onClick={handleCopy} leftIcon={<MdContentCopy />} ml={4} flexShrink={0}>
                Copier
            </Button>
        </Flex>
    );
};

// ─── Per-platform documentation content ───────────────────────────────────────
const PLATFORM_DOCS = {
    google_sheets: {
        title: 'Documentation de Modification Google Sheets',
        color: 'green',
        icon: <SiGooglesheets size={28} color="#34A853" />,
        connectionLabel: (config) => `Configuration associée : ${config.sheet_name || config.name}`,
        statsLabel: 'commandes modifiées',
        connectionExtra: (webhookUrl, config, colors) => (
            <Box p={3} bg={colors.darkBg} rounded="md" border="1px solid" borderColor={colors.borderC}>
                <Text fontWeight="bold" fontSize="xs" color={colors.subText} mb={1}>
                    🔑 TOKEN SECRET :
                </Text>
                <CopyBlock value={config.webhook_token || '—'} label="Token" />
            </Box>
        ),
        infoAlert: "C'est l'URL qui recevra les modifications depuis Google Sheets. Collez-la dans votre script Apps Script.",
        steps: [
            { step: '1', title: 'Créer un Google Sheets', content: 'Créez une nouvelle feuille de calcul sur Google Sheets.' },
            { step: '2', title: "Ouvrir l'éditeur de script", content: "Dans votre Google Sheets, cliquez sur 'Extensions' puis sur 'Apps Script'." },
            { step: '3', title: 'Copier le script', content: "Supprimez le code existant dans l'éditeur et collez le script fourni dans l'onglet 'Documentation'." },
            { step: '4', title: 'Créer un Déclencheur (TRÈS IMPORTANT)', content: "Dans le menu de gauche de l'éditeur de script, cliquez sur l'icône de l'horloge ('Déclencheurs'). Cliquez sur 'Ajouter un déclencheur' en bas à droite. Configurez comme suit :\n• Fonction à exécuter : handleCellEdit\n• Déploiement : Head\n• Source de l'événement : Depuis la feuille de calcul\n• Type d'événement : Lors d'une modification\nCliquez sur Enregistrer et autorisez l'accès avec votre compte Google." },
            { step: '5', title: 'Sauvegarder et Initialiser', content: "Retournez à l'éditeur (<>). Sélectionnez la fonction 'setupHeaders' en haut, et cliquez sur 'Exécuter'. Cela va colorer et formater vos colonnes correctement, et configurer les listes déroulantes strictes pour les statuts." },
            { step: '6', title: 'Modifier les statuts', content: 'Vous pouvez maintenant modifier les statuts de vos commandes dans les colonnes correspondantes. Les changements seront envoyés automatiquement à Ecolinke.' },
        ],
        hasScript: true,
        hasDownload: true,
        examplePayload: `{
  "row_data": {
    "Code Commande": "CMD-12345",
    "Nom complet": "Jean Dupont",
    "téléphone": "0612345678",
    "statut de confirmation": "Confirmé",
    "statut stock": "Collecté",
    "statut livraison": "Livré",
    "statut facture": "Payé",
    "prix": "199.99"
  },
  "source_row": 2,
  "timestamp": "2026-05-17T12:00:00Z"
}`,
        commonErrors: [
            { code: 'Code Commande manquant', solution: "La ligne modifiée ne contient pas de code commande dans la colonne A. Vérifiez que la colonne 'A' n'est pas vide." },
            { code: 'Commande non trouvée', solution: "Le système n'a pas pu trouver une commande avec le code indiqué. Vérifiez que le code correspond exactement." },
            { code: 'Non autorisé à modifier cette commande', solution: "Vous essayez de modifier une commande qui n'appartient pas à votre business." },
            { code: 'Coupon introuvable', solution: "Le code de réduction saisi n'existe pas ou a été désactivé. Créez le coupon dans Ecolinke d'abord." },
        ],
    },

    codnetwork: {
        title: 'Documentation CODNetwork Seller',
        color: 'blue',
        icon: <FaNetworkWired size={28} color="#3182ce" />,
        connectionLabel: () => 'Intégrez CODNetwork Seller pour mettre à jour automatiquement vos commandes.',
        statsLabel: 'commandes modifiées',
        connectionExtra: null,
        infoAlert: "À chaque mise à jour de statut sur CODNetwork, une requête POST est envoyée automatiquement à cette URL. Ecolinke reçoit les données et met à jour la commande correspondante en temps réel.",
        steps: [
            { step: '1', title: "Copier l'URL Webhook", content: "Copiez l'URL Webhook affichée dans l'onglet \"Connexion\" ci-dessus." },
            { step: '2', title: 'Accéder à votre compte CODNetwork Seller', content: "Connectez-vous à votre tableau de bord CODNetwork Seller sur le site officiel." },
            { step: '3', title: 'Aller dans les paramètres Webhook', content: "Dans le menu, allez dans Settings → Webhook (ou API & Intégrations)." },
            { step: '4', title: 'Ajouter un Webhook', content: "Cliquez sur 'Ajouter un Webhook'. Dans le champ URL, collez l'URL copiée à l'étape 1." },
            { step: '5', title: 'Sélectionner les événements', content: "Sélectionnez les événements :\n• order/update (mise à jour de commande)\n• status_change (changement de statut)" },
            { step: '6', title: 'Enregistrer et tester', content: "Enregistrez le webhook. Vous pouvez utiliser le bouton 'Test' de CODNetwork pour vérifier que la connexion fonctionne." },
        ],
        hasScript: false,
        hasDownload: false,
        examplePayload: `{
  "order_id": "CMD-12345",
  "status": "Livré",
  "delivery_status": "Livré",
  "confirmation_status": "Confirmé",
  "stock_status": "Collecté",
  "invoice_status": "Payé",
  "timestamp": "2026-06-11T12:00:00Z"
}`,
        commonErrors: [
            { code: 'order_id manquant', solution: "Le payload reçu ne contient pas de champ 'order_id'. Vérifiez la configuration du webhook côté CODNetwork." },
            { code: 'Commande non trouvée', solution: "Aucune commande ne correspond à l'identifiant transmis. Assurez-vous que le code commande dans Ecolinke correspond à celui de CODNetwork." },
            { code: 'Non autorisé', solution: "La requête ne correspond pas au token de sécurité de cette connexion. Vérifiez que vous utilisez la bonne URL webhook." },
            { code: 'Statut non reconnu', solution: "La valeur de statut transmise par CODNetwork n'est pas dans la liste des statuts acceptés par Ecolinke." },
        ],
    },
};

// ─── Google Sheets script template (only used for google_sheets) ───────────────
const getScript = (webhookUrl) => `// ==============================================
// SCRIPT GOOGLE APPS SCRIPT POUR MODIFICATION DES COMMANDES
// ==============================================

// CONFIGURATION - NE MODIFIEZ QUE CETTE LIGNE
const WEBHOOK_URL = "${webhookUrl}";

// Nom de la feuille à surveiller
const SHEET_NAME = "Feuille 1";

// Colonnes attendues
const COLUMNS = {
  A: 'Code Commande',
  B: 'Nom complet',
  C: 'téléphone',
  D: 'Produits',
  E: 'statut de confirmation',
  F: 'statut stock',
  G: 'statut livraison',
  H: 'statut facture',
  I: 'coupon',
  J: 'prix'
};

function handleCellEdit(e) {
  try {
    if (!e || !e.source) return;
    const sheet = e.range.getSheet();

    // Ne s'applique qu'à la feuille principale
    if (sheet.getName() !== SHEET_NAME) return;
    
    const range = e.range;
    const row = range.getRow();
    const col = range.getColumn();

    // Ignorer la ligne d'en-tête
    if (row <= 1) return;

    // Colonnes cibles
    const COL_CONFIRMATION = 5; // E
    const COL_STOCK        = 6; // F
    const COL_LIVRAISON    = 7; // G
    const COL_FACTURE      = 8; // H

    // Validation stricte des valeurs saisies pour les colonnes de statut
    const ALLOWED_CONFIRMATION = ["Nouveau", "Pas de réponse", "Messagerie vocale", "En attente", "Ligne occupée", "Mauvais numéro", "Annulé", "Confirmé"];
    const ALLOWED_STOCK = ["Épuisé", "Annulé", "En cours", "Emballé", "Collecté"];
    const ALLOWED_LIVRAISON = [
      "Ramassé", "Expédié", "Reçu à destination", "Pendant la distribution", 
      "Programmé", "Hors zone", "Non atteint", "Annulé", "Refusé", "Livré", 
      "Préparation du retour", "Retour à l'agence", "Retour à l'entrepôt"
    ];
    const ALLOWED_FACTURE = ["Non payé", "Payé"];

    if (col === COL_CONFIRMATION) {
      const val = range.getValue().toString().trim();
      if (val !== "" && ALLOWED_CONFIRMATION.indexOf(val) === -1) {
        rejectInvalidValue(row, range, val, "statut de confirmation", e.oldValue);
        return;
      }
    } else if (col === COL_STOCK) {
      const val = range.getValue().toString().trim();
      if (val !== "" && ALLOWED_STOCK.indexOf(val) === -1) {
        rejectInvalidValue(row, range, val, "statut stock", e.oldValue);
        return;
      }
    } else if (col === COL_LIVRAISON) {
      const val = range.getValue().toString().trim();
      if (val !== "" && ALLOWED_LIVRAISON.indexOf(val) === -1) {
        rejectInvalidValue(row, range, val, "statut livraison", e.oldValue);
        return;
      }
    } else if (col === COL_FACTURE) {
      const val = range.getValue().toString().trim();
      if (val !== "") {
        const lowerVal = val.toLowerCase();
        if (lowerVal === "payé" || lowerVal === "paye") {
          range.setValue("Payé");
        } else if (lowerVal === "non payé" || lowerVal === "non paye") {
          range.setValue("Non payé");
        } else if (ALLOWED_FACTURE.indexOf(val) === -1) {
          rejectInvalidValue(row, range, val, "statut facture", e.oldValue);
          return;
        }
      }
    }

    // ── Règle 6 : Alerte de calcul automatique du prix de vente ──
    const COL_PRODUITS = 4; // D
    let autoCalculate = false;
    if (col === COL_PRODUITS) {
      const valProd = sheet.getRange(row, COL_PRODUITS).getValue().toString().trim();
      if (valProd !== "") {
        const ui = SpreadsheetApp.getUi();
        const response = ui.alert(
          'Calcul du prix',
          'Voulez-vous que le système calcule automatiquement la somme des prix de ces produits ?\\n\\n' +
          '- Cliquez sur "Oui" pour utiliser la somme des prix de la base de données.\\n' +
          '- Cliquez sur "Non" si vous préférez définir vous-même le prix de vente dans la colonne "prix".',
          ui.ButtonSet.YES_NO
        );
        if (response === ui.Button.YES) {
          autoCalculate = true;
        }
      }
    }
    
    // ── Règle 5 : Arrondi automatique du PRIX à 2 décimales ──
    const COL_PRIX = 10; // J
    if (col === COL_PRIX) {
      const cell = sheet.getRange(row, COL_PRIX);
      const val = parseFloat(cell.getValue());
      if (!isNaN(val)) {
        cell.setValue(Math.round(val * 100) / 100);
      }
    }

    const valConf = sheet.getRange(row, COL_CONFIRMATION).getValue();
    const valStock = sheet.getRange(row, COL_STOCK).getValue();
    const valLivr  = sheet.getRange(row, COL_LIVRAISON).getValue();

    // ── Règle 4 : Promotion automatique si modification de la FACTURE ──
    if (col === COL_FACTURE) {
      const valFact = sheet.getRange(row, COL_FACTURE).getValue().toString().trim().toLowerCase();
      if (valFact === "payé" || valFact === "paye" || valFact === "non payé" || valFact === "non paye") {
        sheet.getRange(row, COL_CONFIRMATION).setValue("Confirmé");
        sheet.getRange(row, COL_STOCK).setValue("Collecté");
        sheet.getRange(row, COL_LIVRAISON).setValue("Livré");
      }
    }

    // ── Règle 2 : Promotion automatique si modification du STOCK ──
    else if (col === COL_STOCK) {
      sheet.getRange(row, COL_CONFIRMATION).setValue("Confirmé");
    }

    // ── Règle 3 : Promotion automatique si modification de la LIVRAISON ──
    else if (col === COL_LIVRAISON) {
      sheet.getRange(row, COL_CONFIRMATION).setValue("Confirmé");
      sheet.getRange(row, COL_STOCK).setValue("Collecté");
    }

    // Récupérer les valeurs finales après promotion
    const finalConf = sheet.getRange(row, COL_CONFIRMATION).getValue().toString().trim();
    const finalStock = sheet.getRange(row, COL_STOCK).getValue().toString().trim();
    const finalLivr = sheet.getRange(row, COL_LIVRAISON).getValue().toString().trim();

    // ── GESTION DES STATUTS ET VIDAGE EN CASCADE ──
    
    // 1. Si le statut de confirmation != "Confirmé" -> Vider tout le reste
    if (finalConf !== "Confirmé") {
      sheet.getRange(row, COL_STOCK).clearContent();
      sheet.getRange(row, COL_LIVRAISON).clearContent();
      sheet.getRange(row, COL_FACTURE).clearContent();
    }
    // 2. Si le statut de stock != "Collecté" -> Vider livraison et facture
    else if (finalStock !== "Collecté") {
      sheet.getRange(row, COL_LIVRAISON).clearContent();
      sheet.getRange(row, COL_FACTURE).clearContent();
    }
    // 3. Si le statut de livraison != "Livré" -> Vider facture
    else if (finalLivr !== "Livré") {
      sheet.getRange(row, COL_FACTURE).clearContent();
    }

    // Récupérer les données mises à jour
    const rowData = getRowData(sheet, row);
    if (!isRowComplete(rowData)) return;
    
    sendToWebhook(rowData, row, autoCalculate);
    
  } catch (error) {
  }
}

function getRowData(sheet, row) {
  const data = {};
  for (let col in COLUMNS) {
    const cellValue = sheet.getRange(col + row).getValue();
    const fieldName = COLUMNS[col];
    
    if (cellValue !== null && cellValue !== "") {
      data[fieldName] = cellValue.toString().trim();
    }
  }
  return data;
}

function isRowComplete(rowData) {
  // Seul le Code Commande est strictement requis
  const requiredFields = ['Code Commande'];
  return requiredFields.every(field => rowData[field] && rowData[field] !== "");
}

function sendToWebhook(rowData, row, autoCalculate) {
  try {
    const payload = {
      row_data: rowData,
      source_row: row,
      auto_calculate_price: autoCalculate || false,
      timestamp: new Date().toISOString()
    };
    
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      payload: JSON.stringify(payload),
      muteHttpExceptions: true
    };
    
    const response = UrlFetchApp.fetch(WEBHOOK_URL, options);
    const responseText = response.getContentText();
    const responseCode = response.getResponseCode();
    
    if (responseCode === 200) {
      const result = JSON.parse(responseText);
      if (result.success) {
        markRowAsProcessed(row, "✅ Envoyé");
        if (result.price !== undefined && result.price !== null) {
          const sheet = SpreadsheetApp.getActiveSheet();
          sheet.getRange(row, 10).setValue(result.price);
        }
      } else {
        markRowAsProcessed(row, "❌ Erreur: " + (result.message || result.error));
      }
    } else {
      markRowAsProcessed(row, "❌ Erreur HTTP " + responseCode);
    }
    
  } catch (error) {
    markRowAsProcessed(row, "❌ Erreur script: " + error.message);
  }
}

function markRowAsProcessed(row, status) {
  try {
    const sheet = SpreadsheetApp.getActiveSheet();
    sheet.getRange("K" + row).setValue(status);
    sheet.getRange("L" + row).setValue(new Date());
  } catch (error) {
  }
}

function rejectInvalidValue(row, range, val, fieldLabel, oldValue) {
  range.setValue(oldValue || "");
  markRowAsProcessed(row, "❌ " + fieldLabel + " invalide");
  try {
    const ui = SpreadsheetApp.getUi();
    ui.alert(
      "Valeur non autorisée",
      "Erreur : La valeur '" + val + "' n'est pas acceptée pour le '" + fieldLabel + "'. Veuillez choisir une option de la liste déroulante.",
      ui.ButtonSet.OK
    );
  } catch(e) {}
}

function setupHeaders() {
  const sheet = SpreadsheetApp.getActiveSheet();
  const headers = [
    "Code Commande", "Nom complet", "téléphone", "Produits", 
    "statut de confirmation", "statut stock", "statut livraison", "statut facture", 
    "coupon", "prix",
    "Statut", "Date traitement"
  ];
  
  const headerRange = sheet.getRange(1, 1, 1, headers.length);
  headerRange.setValues([headers]);
  headerRange.setBackground("#4285f4");
  headerRange.setFontColor("white");
  headerRange.setFontWeight("bold");
  sheet.autoResizeColumns(1, headers.length);
  
  // Toujours formater la colonne prix (J) pour avoir 2 chiffres après la virgule
  sheet.getRange("J2:J").setNumberFormat("0.00");

  // Configurer la validation des données (listes déroulantes strictes)
  const validationConf = SpreadsheetApp.newDataValidation()
    .requireValueInList(["Nouveau", "Pas de réponse", "Messagerie vocale", "En attente", "Ligne occupée", "Mauvais numéro", "Annulé", "Confirmé"], true)
    .setAllowInvalid(false)
    .build();
  sheet.getRange("E2:E").setDataValidation(validationConf);

  const validationStock = SpreadsheetApp.newDataValidation()
    .requireValueInList(["Épuisé", "Annulé", "En cours", "Emballé", "Collecté"], true)
    .setAllowInvalid(false)
    .build();
  sheet.getRange("F2:F").setDataValidation(validationStock);

  const validationLivr = SpreadsheetApp.newDataValidation()
    .requireValueInList([
      "Ramassé", "Expédié", "Reçu à destination", "Pendant la distribution", 
      "Programmé", "Hors zone", "Non atteint", "Annulé", "Refusé", "Livré", 
      "Préparation du retour", "Retour à l'agence", "Retour à l'entrepôt"
    ], true)
    .setAllowInvalid(false)
    .build();
  sheet.getRange("G2:G").setDataValidation(validationLivr);

  const validationFacture = SpreadsheetApp.newDataValidation()
    .requireValueInList(["Non payé", "Payé"], true)
    .setAllowInvalid(false)
    .build();
  sheet.getRange("H2:H").setDataValidation(validationFacture);
}`;

// ─── Fallback doc for unknown platforms ───────────────────────────────────────
const FALLBACK_DOC = {
    title: 'Documentation Webhook',
    color: 'purple',
    icon: <MdApi size={28} color="#805AD5" />,
    connectionLabel: (config) => config.name,
    statsLabel: 'commandes modifiées',
    connectionExtra: null,
    infoAlert: "Collez cette URL dans les paramètres Webhook de votre plateforme.",
    steps: [
        { step: '1', title: 'Copier l\'URL Webhook', content: "Copiez l'URL Webhook ci-dessous." },
        { step: '2', title: 'Configurer votre plateforme', content: "Collez l'URL dans les paramètres Webhook de votre plateforme." },
        { step: '3', title: 'Tester', content: "Effectuez un test pour vérifier que la connexion fonctionne." },
    ],
    hasScript: false,
    hasDownload: false,
    examplePayload: `{ "order_id": "...", "status": "..." }`,
    commonErrors: [
        { code: '401 Unauthorized', solution: "Vérifiez que l'URL webhook contient le bon token." },
    ],
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function Gestion_Webhook_Modify_Doc() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const [config] = useState(state?.config || null);

    const subText    = useColorModeValue('gray.500', 'gray.400');
    const cardBg     = useColorModeValue('white', 'gray.700');
    const borderC    = useColorModeValue('gray.200', 'gray.600');
    const darkBg     = useColorModeValue('gray.900', 'gray.800');

    if (!config) {
        return (
            <Flex justify="center" align="center" minH="300px" direction="column" gap={4}>
                <Text>Aucune connexion sélectionnée.</Text>
                <Button onClick={() => navigate('/modifier-commandes-webhook')}>
                    Retour aux configurations
                </Button>
            </Flex>
        );
    }

    // ── Resolve doc based on platform code ─────────────────────────────────
    const doc = PLATFORM_DOCS[config.platform] || FALLBACK_DOC;
    const colorScheme = doc.color;
    const webhookUrl = config.webhook_url || '';

    const copyToast = (text, label) => {
        navigator.clipboard.writeText(text);
        toast({ description: `${label} copié !`, status: 'success', duration: 2000 });
    };

    return (
        <Box p={{ base: 4, md: 8 }} maxW="1000px" mx="auto">

            {/* Back button */}
            <Button
                leftIcon={<MdArrowBack />}
                variant="ghost"
                mb={6}
                onClick={() => navigate('/modifier-commandes-webhook')}
                size="sm"
            >
                Retour aux configurations
            </Button>

            {/* Header card */}
            <Box bg={cardBg} border="1px solid" borderColor={borderC} rounded="2xl" p={6} mb={6} shadow="sm">
                <Flex justify="space-between" align="flex-start" direction={{ base: 'column', md: 'row' }} gap={4}>
                    <Box>
                        <Flex align="center" gap={3} mb={2}>
                            <Box color={`${colorScheme}.500`}>{doc.icon}</Box>
                            <Heading size="lg">{doc.title}</Heading>
                            <Badge colorScheme={config.is_active ? 'green' : 'red'} rounded="full" px={3}>
                                {config.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                        </Flex>
                        <Text color={subText} fontSize="sm">
                            {doc.connectionLabel(config)}
                        </Text>
                    </Box>
                    <HStack spacing={4}>
                        <Tag colorScheme={colorScheme} size="md" rounded="full">
                            <TagLeftIcon as={MdCheckCircle} />
                            <TagLabel>{config.total_orders_modified || 0} {doc.statsLabel}</TagLabel>
                        </Tag>
                    </HStack>
                </Flex>
            </Box>

            {/* Tabs */}
            <Tabs colorScheme={colorScheme} variant="enclosed-colored">
                <TabList flexWrap="wrap">
                    <Tab>🔗 Connexion</Tab>
                    <Tab>📖 Documentation</Tab>
                    <Tab>📦 Payload exemple</Tab>
                    <Tab>❗ Erreurs courantes</Tab>
                </TabList>

                <TabPanels>
                    {/* ── Tab 1: Connexion ─────────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Stack spacing={6}>
                            <Box>
                                <Text fontWeight="bold" mb={2} fontSize="sm" textTransform="uppercase" color={subText}>
                                    🌐 URL Webhook
                                </Text>
                                <CopyBlock value={webhookUrl} label="URL Webhook" />
                                <Text fontSize="xs" color={subText} mt={1}>
                                    {doc.infoAlert}
                                </Text>
                            </Box>

                            {/* Extra content (e.g. token for Google Sheets) */}
                            {doc.connectionExtra && (
                                <>
                                    <Divider />
                                    {doc.connectionExtra(webhookUrl, config, { darkBg, borderC, subText })}
                                </>
                            )}
                        </Stack>
                    </TabPanel>

                    {/* ── Tab 2: Documentation ─────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Stack spacing={5}>
                            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                                <Heading size="md">Guide d'intégration pas à pas</Heading>
                                {doc.hasDownload && (
                                    <Button
                                        as="a"
                                        href={templateFile}
                                        download="orders_to_update_webhook.csv"
                                        leftIcon={<MdDownload size={18} />}
                                        bg="blue.50"
                                        color="blue.700"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        _hover={{ bg: 'blue.100', transform: 'translateY(-1px)', shadow: 'sm' }}
                                        transition="all 0.2s"
                                        size="md"
                                        rounded="xl"
                                        px={6}
                                        fontWeight="semibold"
                                    >
                                        Télécharger le Modèle Excel
                                    </Button>
                                )}
                            </Flex>

                            {doc.steps.map((s) => (
                                <Box key={s.step} bg={cardBg} border="1px solid" borderColor={borderC} rounded="xl" p={5}>
                                    <Flex gap={4} align="flex-start">
                                        <Box
                                            bg={`${colorScheme}.500`}
                                            color="white"
                                            rounded="full"
                                            w={8} h={8}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontWeight="bold"
                                            fontSize="sm"
                                            flexShrink={0}
                                        >
                                            {s.step}
                                        </Box>
                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>{s.title}</Text>
                                            <Text fontSize="sm" color={subText} whiteSpace="pre-line">{s.content}</Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            ))}

                            {/* Script block — only for google_sheets */}
                            {doc.hasScript && (
                                <Box>
                                    <Text fontWeight="bold" mb={2}>Script Google Apps Script :</Text>
                                    <Box bg={darkBg} p={4} rounded="xl" position="relative">
                                        <Code
                                            display="block"
                                            whiteSpace="pre"
                                            fontSize="xs"
                                            color="green.300"
                                            bg="transparent"
                                            overflowX="auto"
                                        >
                                            {getScript(webhookUrl)}
                                        </Code>
                                        <Button
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            size="xs"
                                            onClick={() => copyToast(getScript(webhookUrl), 'Script')}
                                        >
                                            <MdContentCopy />
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </TabPanel>

                    {/* ── Tab 3: Payload exemple ───────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Heading size="md" mb={4}>Exemple de payload</Heading>
                        <Alert status="info" rounded="lg" mb={4} py={2}>
                            <AlertIcon as={MdInfo} />
                            <AlertDescription fontSize="sm">
                                Structure JSON transmise lors de chaque mise à jour.
                            </AlertDescription>
                        </Alert>
                        <Box bg={darkBg} p={4} rounded="xl" position="relative">
                            <Code display="block" whiteSpace="pre" fontSize="xs" color="cyan.300" bg="transparent" overflowX="auto">
                                {doc.examplePayload}
                            </Code>
                            <Button
                                position="absolute"
                                top={2}
                                right={2}
                                size="xs"
                                onClick={() => copyToast(doc.examplePayload, 'Payload')}
                            >
                                <MdContentCopy />
                            </Button>
                        </Box>
                    </TabPanel>

                    {/* ── Tab 4: Erreurs courantes ─────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Heading size="md" mb={4}>Erreurs courantes et solutions</Heading>
                        <Stack spacing={3}>
                            {doc.commonErrors.map((err, i) => (
                                <Box key={i} bg={cardBg} border="1px solid" borderColor="red.200" rounded="xl" p={4}>
                                    <Flex gap={3} align="flex-start">
                                        <Box color="red.400" mt={0.5} flexShrink={0}><MdWarning size={20} /></Box>
                                        <Box>
                                            <Code colorScheme="red" mb={1} fontSize="sm">{err.code}</Code>
                                            <Text fontSize="sm" color={subText}>{err.solution}</Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            ))}
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
